import React, { useState, useContext } from 'react';
import { Home, PlusCircle, List, BarChart2, Bell, Settings, LogOut, User, Menu, X } from 'lucide-react';
import { FirebaseContext } from '../App';

const Header = ({ userId, setCurrentPage }) => {
    const { handleLogout } = useContext(FirebaseContext);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
        if (!isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    };

    const handleMenuItemClick = (page) => {
        setCurrentPage(page);
        setIsMobileMenuOpen(false);
        document.body.style.overflow = 'unset';
    };

    return (
        <header className="bg-white shadow-md py-4 px-6 sticky top-0 z-20">
            <div className="container mx-auto flex justify-between items-center">
                {/* ロゴ */}
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <BarChart2 className="w-8 h-8 text-blue-500" />
                    みんなの会計 MoneyBoard
                </h1>

                {/* モバイル用ハンバーガーメニューアイコン */}
                <div className="md:hidden">
                    <button onClick={toggleMobileMenu} className="text-gray-600 hover:text-blue-600 focus:outline-none">
                        {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                    </button>
                </div>

                {/* PC用ナビゲーション */}
                <nav className="hidden md:block">
                    <ul className="flex space-x-6">
                        <li>
                            <button onClick={() => setCurrentPage('dashboard')} className="flex items-center gap-2 text-gray-600 hover:text-blue-600 font-medium transition duration-200 ease-in-out transform hover:scale-105">
                                <Home size={20} />
                                ダッシュボード
                            </button>
                        </li>
                        <li>
                            <button onClick={() => setCurrentPage('transactionInput')} className="flex items-center gap-2 text-gray-600 hover:text-blue-600 font-medium transition duration-200 ease-in-out transform hover:scale-105">
                                <PlusCircle size={20} />
                                取引入力
                            </button>
                        </li>
                        <li>
                            <button onClick={() => setCurrentPage('transactionList')} className="flex items-center gap-2 text-gray-600 hover:text-blue-600 font-medium transition duration-200 ease-in-out transform hover:scale-105">
                                <List size={20} />
                                取引一覧
                            </button>
                        </li>
                        <li>
                            <button onClick={() => setCurrentPage('reports')} className="flex items-center gap-2 text-gray-600 hover:text-blue-600 font-medium transition duration-200 ease-in-out transform hover:scale-105">
                                <BarChart2 size={20} />
                                レポート
                            </button>
                        </li>
                        <li>
                            <button onClick={() => setCurrentPage('settings')} className="flex items-center gap-2 text-gray-600 hover:text-blue-600 font-medium transition duration-200 ease-in-out transform hover:scale-105">
                                <Settings size={20} />
                                設定
                            </button>
                        </li>
                        <li>
                            <button onClick={handleLogout} className="flex items-center gap-2 text-red-600 hover:text-red-800 font-medium transition duration-200 ease-in-out transform hover:scale-105">
                                <LogOut size={20} />
                                ログアウト
                            </button>
                        </li>
                    </ul>
                </nav>

                {/* ユーザーID表示 (PC版のみ、またはモバイルではメニュー内に移動) */}
                <div className="hidden md:flex items-center gap-3 text-gray-600">
                    <User size={20} />
                    <span className="font-medium text-sm">ユーザーID: {userId ? userId.substring(0, 8) + '...' : '未認証'}</span>
                </div>
            </div>

            {/* モバイルメニュー（オーバーレイ） */}
            <div className={`fixed inset-0 bg-black bg-opacity-75 z-40 transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out md:hidden`}>
                <div className="bg-white w-3/4 h-full shadow-lg p-6 flex flex-col">
                    <div className="flex justify-between items-center mb-6 border-b pb-4">
                        <h2 className="text-2xl font-bold text-gray-800">メニュー</h2>
                        <button onClick={toggleMobileMenu} className="text-gray-600 hover:text-red-600 focus:outline-none">
                            <X size={28} />
                        </button>
                    </div>
                    <nav className="flex-grow">
                        <ul className="space-y-4">
                            <li>
                                <button onClick={() => handleMenuItemClick('dashboard')} className="flex items-center gap-3 text-gray-700 hover:text-blue-600 font-medium text-lg w-full text-left p-2 rounded-md hover:bg-blue-50 transition duration-200 ease-in-out">
                                    <Home size={24} />
                                    ダッシュボード
                                </button>
                            </li>
                            <li>
                                <button onClick={() => handleMenuItemClick('transactionInput')} className="flex items-center gap-3 text-gray-700 hover:text-blue-600 font-medium text-lg w-full text-left p-2 rounded-md hover:bg-blue-50 transition duration-200 ease-in-out">
                                    <PlusCircle size={24} />
                                    取引入力
                                </button>
                            </li>
                            <li>
                                <button onClick={() => handleMenuItemClick('transactionList')} className="flex items-center gap-3 text-gray-700 hover:text-blue-600 font-medium text-lg w-full text-left p-2 rounded-md hover:bg-blue-50 transition duration-200 ease-in-out">
                                    <List size={24} />
                                    取引一覧
                                </button>
                            </li>
                            <li>
                                <button onClick={() => handleMenuItemClick('reports')} className="flex items-center gap-3 text-gray-700 hover:text-blue-600 font-medium text-lg w-full text-left p-2 rounded-md hover:bg-blue-50 transition duration-200 ease-in-out">
                                    <BarChart2 size={24} />
                                    レポート
                                </button>
                            </li>
                            <li>
                                <button onClick={() => handleMenuItemClick('settings')} className="flex items-center gap-3 text-gray-700 hover:text-blue-600 font-medium text-lg w-full text-left p-2 rounded-md hover:bg-blue-50 transition duration-200 ease-in-out">
                                    <Settings size={24} />
                                    設定
                                </button>
                            </li>
                            <li className="mt-6 border-t pt-4">
                                <button onClick={handleLogout} className="flex items-center gap-3 text-red-600 hover:text-red-800 font-medium text-lg w-full text-left p-2 rounded-md hover:bg-red-50 transition duration-200 ease-in-out">
                                    <LogOut size={24} />
                                    ログアウト
                                </button>
                            </li>
                        </ul>
                    </nav>
                    {/* モバイルメニュー内のユーザーID表示 */}
                    <div className="mt-auto flex items-center gap-3 text-gray-600 border-t pt-4">
                        <User size={20} />
                        <span className="font-medium text-sm">ユーザーID: {userId ? userId.substring(0, 8) + '...' : '未認証'}</span>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
